/**
 * @format
 * @module lib/commercial/keytar/common/takeover
 * @description Shared methods for displaying overlay walls.
 * Which appear as overlays blocking the page, except for the site header.
 */
import { getById, toArray, qs, removeNode, qsa, getProp } from "utils/utils";
import tgamConfig from "config/tgam";
import CONSTS from "lib/consts";
import isFunction from "lodash/isFunction";
import { uNoScrollClass } from "ui/site-header";
import isNil from "lodash/isNil";

const URLS = {
  close: tgamConfig.env.baseHomepageUrl,
  home: tgamConfig.env.baseHomepageUrl,
  subscribe: CONSTS.URLS.SUBSCRIBE.DIGITAL,
  img: {
    main: `${tgamConfig.env.baseRootAbsoluteUrl}/files/cem/gufunnel/gu-header.png`,
    logo: `${tgamConfig.env.svgSpritePath}#tgam-logo-boxed` // same as site header logo
  }
};

// Same headline and internal campaign values for takeover and teaser paywalls
export const templateParams = {
  hardPaywall: {
    headline: "This content is available to <span>globeandmail.com</span> subscribers.",
    intcmp: "redwall"
  },
  meteredPaywall: {
    headline:
      "You've reached your free article limit. To keep reading, subscribe to <span>globeandmail.com</span>",
    intcmp: "threshwall"
  }
};

/**
 * Remove all children elements from the PB Root element.
 * Expect for #top-furniture-area, which is the site header.
 * @param {Array<String>} [leave=[]] - array of element ids, which should not be removed.
 */
export function removeRootElements(leave = []) {
  const pbRoot = getById("pb-root");
  if (pbRoot) {
    // Prevent scrolling for: mobile, tablet, desktop
    // Also prevents multiple GTM `scrollEvent`'s from being generated, due to removing the children elements.
    document.body.classList.add(uNoScrollClass);

    // Apply a min-height to pbRoot element, before removing all the elements from it.
    // In order to prevent undesired analytics scrollEvents from being generated.
    pbRoot.style.minHeight = window.getComputedStyle(pbRoot).height;

    // Kill all the younglings ...
    toArray(pbRoot.children)
      .filter(({ id }) => leave.indexOf(id) === -1)
      .forEach(removeNode);
  }
}

/**
 * Hide all elements that match the selectors
 * @param {Array<String>} [selectors=[]] - array of element ids, classes, tags to hide
 */
export function hideRootElements(selectors = []) {
  const pbRoot = getById("pb-root");
  if (pbRoot) {
    selectors.forEach(selector => {
      const nodes = qsa(selector, pbRoot);
      nodes.forEach(node => node.classList.add("u-hidden"));
    });
  }
}

/**
 * Select the parent container that will wrap the main content element.
 * @param {Array<String>} [appendClass=[]] - CSS classes to append to the element.
 * @returns {HTMLElement}
 */
export function setupContainer(appendClass = []) {
  const container = qs(".c-keytar-container--modal");
  if (isNil(container)) {
    return null;
  }
  appendClass.forEach(klass => {
    container.classList.add(`c-keytar-container--modal--${klass}`);
  });
  container.removeAttribute("hidden");
  container.style.display = "block";
  return container;
}

/**
 * Select the main content that will display the template markup.
 * @param {Array<String>} [appendClass=[]] - CSS classes to append to the element.
 * @returns {HTMLElement}
 */
export function setupMain(appendClass = []) {
  const main = qs(".c-keytar-container__main");
  main.id = "skip-link-target"; // Set accessibility since it removes the article content

  // class used by Nightwatch tests
  main.classList.add("c-keytar-container__main--active");

  appendClass.forEach(klass => {
    main.classList.add(`c-keytar-container__main--${klass}`);
  });
  return main;
}

/**
 * Generate the HTML template for a takeover paywall.
 * @todo Get Template via XHR from Dropbox or PageBuilder page.
 * @param {Object} c - collection or properties
 * @param {String} [c.headline=""] - displayed at top of paywall
 * @param {String} [c.intcmp=""] - URL parameter value for subscription funnel links (internal campaign)
 * @returns {String} of HTML markup to be inserted in the DOM
 */
export function paywallTemplate({ headline = "", intcmp = "" }) {
  const campaign = intcmp.length ? `?intcmp=${intcmp}` : "";
  return `
    <div class="c-keytar-header">
      <p class="c-keytar-header__text">${headline}</p>
      <a aria-label="Close paywall message"
        class="c-button c-button--no-border c-button--icon-only c-button--transparent-default js-keytar-sub-btn-close"
        href="${URLS.close}">
        <svg
          class="c-button__icon"
          role="img"
          pointer-events="none"
          focusable="false"
          aria-hidden="true">
          <use xlink:href="${tgamConfig.env.svgSpritePath}#close-x"></use>
        </svg>
      </a>
    </div>
    <div class="c-keytar-content">
      <h2 class="arc-keytar-wall__main-title font__pratt">
        Join a national community of curious and ambitious Canadians
      </h2>
      <div class="arc-keytar-wall__content">
        <div class="arc-keytar-wall__offer">
          <p class="arc-keytar-wall__offer-title font__pratt">
            Subscribe to globeandmail.com for unlimited access to Canada’s leading independent journalism.
          </p>
          <p class="arc-keytar-wall__offer-cost">
            <span class="arc-keytar-wall__offer-cost-inner">
              <span class="arc-keytar-wall__offer-price font__pratt">Just</span>
              <span class="arc-keytar-wall__offer-price font__pratt">$1.99</span>
            </span>
            <span class="arc-keytar-wall__offer-per font__gmsanc-regular">per week for first 24 weeks</span>
          </p>
          <a href="${URLS.subscribe}${campaign}"
            class="arc-keytar-wall__btn c-keytar-action__btn c-button c-button--utility-yellow-secondary c-button--no-border js-keytar-algt">
            <span class="c-button__text">Subscribe Now</span>
          </a>
          <span class="arc-keytar-wall__cancel-label" class="font__gmsanc-regular">Cancel anytime</span>
        </div>
        <div class="arc-keytar-wall__image"></div>
        <img class="arc-keytar-wall__logo" src="https://www.theglobeandmail.com/files/cem/logos/pti_logo.png" alt="Power To The Informed" />
      </div>
    </div>
    <div class="c-keytar-footer">
      <div class="c-keytar-footer__content">
        <p>
          Already a print newspaper subscriber?
          <a href="${CONSTS.URLS.LINK_PRINT_ACCOUNT}" class="js-keytar-algt">
            Get full access to globeandmail.com
          </a>
        </p>
        <p>
          Already subscribed to globeandmail.com?
          <a href="${CONSTS.URLS.LOGIN}" class="js-keytar-algt">
            Log in to keep reading
          </a>
        </p>
      </div>
    </div>
  `;
}

/**
 * @method setUpModalView
 * @description creates a modal wall that will be either the default paywall or the result of darwin function
 * @param {String} wallName - wall name type
 * @param {Function} darwinTemplate - darwin template function
 * @param {Array<String>} [mainClasses=[]] - CSS classes to append to the element.
 * @param {Array<String>} [elementsToRemove=[]] - what elements to remove from the page
 * @param {Array<String>} [containerClasses=[]] - what classes to add to the container
 * @param {String} [url=""] - for iframe if provided in darwin test
 */
export function setUpModalView(
  wallName,
  darwinTemplate,
  mainClasses = [],
  elementsToRemove = [],
  containerClasses = [],
  url = ""
) {
  const html = getModalMarkup(wallName, darwinTemplate, url);
  const main = setupMain(mainClasses);
  main.innerHTML = html;
  removeRootElements(elementsToRemove);
  setupContainer(containerClasses);
}

/**
 * @method getModalMarkup
 * @description Get the template mark to render in the modal.
 * @param {String} wallName - to determine if iframe markup is used or darwin test template
 * @param {Function} darwinTemplate - darwin template function
 * @param {String} [url] - for iframe if provided in darwin test
 * @returns {String} of HTML
 */
function getModalMarkup(wallName, darwinTemplate, url) {
  if (["registerWall", "promotionWall", "hpdcWall", "crosswordsWall"].includes(wallName)) {
    const iframeUrl = getRegwallFrameUrl({ url, wallName });
    const iframeWall = `<iframe id="keytar-iframe" name="keytar-iframe"
      class="js-keytar-iframe js-keytar-${wallName}"
      title="Paywall" scrolling="yes" frameborder="0" allowtransparency="true"
      src="${iframeUrl}"></iframe>`;
    return iframeWall; // show paywall modal with contained iframe
  } else {
    const dwRenderResult = isFunction(darwinTemplate) ? darwinTemplate() : null;
    if (!isNil(dwRenderResult)) {
      return dwRenderResult; // show Darwin rendered paywall
    } else {
      const temp =
        wallName === "hardPaywall" ? templateParams.hardPaywall : templateParams.meteredPaywall;
      return paywallTemplate(temp);
    }
  }
}

/**
 * @method getRegwallFrameUrl
 * @description Get regwall frame url.
 * @param {Object} c - collection of data
 * @param {String} c.url - darwin frame url
 * @param {String} c.wallName
 * @returns {String} of frame url
 */
export function getRegwallFrameUrl({ url, wallName }) {
  const displayDarwinFrame = !isNil(url) && url.length;
  if (displayDarwinFrame) {
    return url;
  }
  const isCoronaArticle = getProp(tgamConfig, "datalayer.article.seoKeywords", "").includes(
    "coronayellow"
  );
  const displayCoronaFrame = wallName.toLowerCase() === "registerwall" && isCoronaArticle;
  return displayCoronaFrame ? CONSTS.URLS.REGISTER.CORONA : CONSTS.URLS.REGISTER.REGWALL;
}
